import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Search, FileText, Trash2 } from "lucide-react";
import api, { money, fmtDate } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge, { STATUSES, statusLabel } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Invoices() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/invoices/${toDelete.id}`);
      setItems((x) => x.filter((i) => i.id !== toDelete.id));
      toast.success("Document deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setToDelete(null);
    }
  };

  const filtered = items.filter((i) => {
    const matchQ = !q || (i.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (i.number || "").toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || i.status === filter || (filter === "estimate" && i.doc_type === "estimate");
    return matchQ && matchF;
  });

  return (
    <Layout
      title="Invoices & Estimates"
      action={
        <Button data-testid="new-invoice-button" onClick={() => navigate("/invoices/new")}
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> New
        </Button>
      }
    >
      <div className="space-y-4 animate-fade-up">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="invoice-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer or #number" className="pl-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[{ value: "all", label: "All" }, ...STATUSES, { value: "estimate", label: "Estimates" }].map((f) => (
              <button key={f.value} data-testid={`filter-${f.value}`} onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  filter === f.value ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-slate-100 border border-slate-800"
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <FileText className="h-10 w-10 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">No documents found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Number</th>
                    <th className="text-left font-semibold px-4 py-3">Customer</th>
                    <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Vehicle</th>
                    <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Date</th>
                    <th className="text-right font-semibold px-4 py-3">Total</th>
                    <th className="text-center font-semibold px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {filtered.map((inv) => (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}
                      className="hover:bg-slate-800/40 transition-colors duration-150 cursor-pointer"
                      onClick={() => navigate(`/invoices/${inv.id}`)}>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-xs text-slate-300">{inv.number}</span>
                        {inv.doc_type === "estimate" && <span className="ml-2 text-[10px] text-blue-400 uppercase">Est</span>}
                      </td>
                      <td className="px-4 py-3.5 font-medium text-slate-100">{inv.customer_name}</td>
                      <td className="px-4 py-3.5 text-slate-400 hidden md:table-cell">
                        {[inv.vehicle_year, inv.vehicle_make, inv.vehicle_model].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td className="px-4 py-3.5 text-slate-400 hidden lg:table-cell">{fmtDate(inv.created_at)}</td>
                      <td className="px-4 py-3.5 text-right font-bold text-slate-100 tabular-nums">{money(inv.total)}</td>
                      <td className="px-4 py-3.5 text-center"><StatusBadge status={inv.status} /></td>
                      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" data-testid={`delete-invoice-${inv.id}`}
                          onClick={() => setToDelete(inv)} className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Delete document?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {toDelete?.number} for {toDelete?.customer_name} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-invoice" onClick={remove} className="bg-rose-600 hover:bg-rose-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
